import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Button } from '@/components/shadcn-ui/button';
import { Badge } from '@/components/shadcn-ui/badge';
import { Input } from '@/components/shadcn-ui/input';
import { ExternalLink, DollarSign } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

const CountyPayment = ({ permitDetails, onUpdate }) => {
    const [countyFees, setCountyFees] = useState('');
    const [loading, setLoading] = useState(false);

    const handleGeneratePaymentLink = async () => {
        if (!countyFees || parseFloat(countyFees) <= 0) {
            toast.warning('Invalid amount', {
                description: 'Please enter a valid county fees amount'
            });
            return;
        }

        setLoading(true);
        try {
            await appAxios.post(`/StripeSetup/v1.0/create-county-fees-payment-link`, {
                priceAmount: parseFloat(countyFees),
                permitId: permitDetails._id,
                currencyCode: "usd"
            });
            toast.success('Payment link generated successfully');
            setCountyFees('');
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Error generating payment link:', error);
            toast.error('Failed to generate payment link', {
                description: error.response?.data?.message || error.message
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 pr-2">
            {/* Base Fees Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Base Fees
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Amount</span>
                        <span className="font-semibold">${permitDetails.base_fees_amount || 0} USD</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Status</span>
                        <Badge variant={permitDetails.base_fees_paid ? "success" : "destructive"}>
                            {permitDetails.base_fees_paid ? 'Paid' : 'Due'}
                        </Badge>
                    </div>
                    {permitDetails?.base_fees_invoice_url && (
                        <a
                            href={permitDetails.base_fees_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 w-fit"
                        >
                            View Invoice
                            <ExternalLink className="h-3 w-3" />
                        </a>

                    )}
                </CardContent>
            </Card>

            {/* County Fees Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        County Fees
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!permitDetails?.base_fees_paid ? (
                        <div className="flex items-center justify-between">
                            Base Fees is pending. Please ask user to pay the base fees to proceed.
                        </div>
                    ) :
                        !permitDetails?.county_fees_payment_link ? (
                            // Generate Payment Link
                            <>
                                <div>
                                    <label className="text-sm font-medium mb-2 block">
                                        Enter County Fees (USD)
                                    </label>
                                    <Input
                                        type="number"
                                        placeholder="Enter amount"
                                        value={countyFees}
                                        onChange={(e) => setCountyFees(e.target.value)}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <Button
                                    className="w-full"
                                    onClick={() => { handleGeneratePaymentLink() }}
                                    disabled={loading || !countyFees}
                                >
                                    {loading ? 'Generating...' : 'Generate Payment Link'}
                                </Button>
                            </>
                        ) : (
                            // Show County Fees Details
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Amount</span>
                                    <span className="font-semibold">
                                        ${permitDetails.county_fees_amount} USD
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Status</span>
                                    <Badge variant={permitDetails.county_fees_paid ? "success" : "destructive"}>
                                        {permitDetails.county_fees_paid ? 'Paid' : 'Due'}
                                    </Badge>
                                </div>
                                {permitDetails.county_fees_paid && permitDetails.county_fees_invoice_url ? (
                                    <a
                                        href={permitDetails.county_fees_invoice_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 w-fit"
                                    >
                                        View Invoice
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                ) : (
                                    <a
                                        href={permitDetails.county_fees_payment_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 w-fit"
                                    >
                                        Payment Link
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                )}
                            </>
                        )}
                </CardContent>
            </Card>
        </div>
    );
};

export default CountyPayment;

